// Fragment "gateway shell" — Payment Gateway v1, the Stage 2 console redesign:
// the four-section navigation, the triage overview, the treasury strip, and the shared
// resource-state vocabulary (`gw.console.state.*`).
//
// OPERATOR LANGUAGE, NOT INTERNAL VOCABULARY. "Trial balance" became "Do the books
// balance", "dead-letter webhooks" became "Events we gave up on", "scope: platform" became
// "Every practice". The rename is a dictionary change; the screens keep their keys.
//
// A new fragment rather than more keys in `gatewayConsole.js`: `mergeFragments` throws on a
// duplicate key, which is what lets a work batch own a file without merge conflicts.
export default {
  fr: {
    "gw.console.section.money": "Argent",
    "gw.console.section.books": "Comptes",
    "gw.console.section.policy": "Règles",
    "gw.console.section.operations": "Exploitation",
    "gw.console.section.overview": "Vue d'ensemble",

    "gw.console.screen.payouts": "Reversements",
    "gw.console.screen.payments": "Paiements",
    "gw.console.screen.disputes": "Litiges",
    "gw.console.screen.adjustments": "Ajustements",
    "gw.console.screen.awaitingOperator": "Encaissements bloqués",
    "gw.console.screen.balances": "Soldes des praticiens",
    "gw.console.screen.reconciliation": "Rapprochement Stripe",
    "gw.console.screen.books": "Les comptes s'équilibrent-ils ?",
    "gw.console.screen.commission": "Commission",
    "gw.console.screen.consultRates": "Tarifs à approuver",
    "gw.console.screen.rules": "Règles de la plateforme",
    "gw.console.screen.disputeRates": "Taux de litige",
    "gw.console.screen.events": "Événements abandonnés",
    "gw.console.screen.consults": "Téléconsultations",

    "gw.console.state.asOf": "au {at}",
    "gw.console.state.neverRead": "jamais chargé",
    "gw.console.state.reading": "lecture…",
    "gw.console.state.refresh": "actualiser",
    "gw.console.state.unreachable": "La passerelle n'a pas répondu.",
    "gw.console.state.staleWarning": "Ces chiffres sont les derniers connus. L'actualisation a échoué : {reason}",

    "gw.console.treasury.title": "Ce que la plateforme détient",
    "gw.console.treasury.colCurrency": "Devise",
    "gw.console.treasury.colOnHold": "En attente de libération",
    "gw.console.treasury.colReady": "Prêt à reverser",
    "gw.console.treasury.colInTransit": "En cours de reversement",
    "gw.console.treasury.colOwed": "Total dû aux praticiens",
    "gw.console.treasury.noCrossCurrency": "Aucun total toutes devises confondues : les montants ne s'additionnent jamais d'une devise à l'autre.",
    "gw.console.treasury.empty": "Aucun solde de praticien pour le moment.",
    "gw.console.treasury.unreadable": "Impossible de lire ce que la plateforme détient. Les montants ne sont pas affichés plutôt qu'affichés à zéro.",
    "gw.console.treasury.debt": "{amount} de dette praticien : les reversements sont bloqués jusqu'à régularisation.",

    "gw.console.overview.title": "Ce qui requiert votre attention",
    "gw.console.overview.go": "ouvrir",
    "gw.console.overview.allClear": "Rien d'autre ne requiert votre attention.",
    "gw.console.overview.booksBalance": "Les comptes s'équilibrent.",
    "gw.console.overview.booksDrift": "LES COMPTES NE S'ÉQUILIBRENT PAS — écart de {amount}.",
    "gw.console.overview.booksUnknown": "Les comptes n'ont pas pu être vérifiés : {reason}",
    "gw.console.overview.disputeNearCliff": "{count} praticien(s) approchent du seuil Stripe : {pct} contre {cliff}.",
    "gw.console.overview.disputeOverCliff": "{count} praticien(s) DÉPASSENT le seuil Stripe de {cliff} : {pct}. Le compte de la plateforme est en risque.",
    "gw.console.overview.disputeRatesUnknown": "Les taux de litige n'ont pas pu être lus : {reason}",
    "gw.console.overview.reconNotRun": "Le rapprochement du {date} n'a pas été effectué.",
    "gw.console.overview.reconProblems": "Rapprochement du {date} : {unmatched} mouvement(s) Stripe sans écriture, {mismatched} écart(s) de montant.",
    "gw.console.overview.reconUnknown": "Le rapprochement du {date} n'a pas pu être lu : {reason}",
    "gw.console.overview.payoutsWaiting": "{count} reversement(s) en attente d'approbation.",
    "gw.console.overview.payoutsUnknown": "La file des reversements n'a pas pu être lue : {reason}",
    "gw.console.overview.eventsGaveUp": "{count} événement(s) Stripe ont épuisé leurs tentatives.",
    "gw.console.overview.eventsUnknown": "Les événements abandonnés n'ont pas pu être lus : {reason}",
    "gw.console.disputeRates.criteria": "Part des encaissements contestés par praticien, sur 90 jours. Alerte interne à {alert} ; Stripe ferme un compte plateforme au-delà de {cliff}.",
    "gw.console.disputeRates.colTenant": "Praticien",
    "gw.console.disputeRates.colCaptured": "Encaissements",
    "gw.console.disputeRates.colDisputed": "Contestés",
    "gw.console.disputeRates.colRate": "Taux",
    "gw.console.disputeRates.colVerdict": "Verdict",
    "gw.console.disputeRates.empty": "Aucun encaissement sur les 90 derniers jours.",
    "gw.console.disputeRates.noVolume": "Des litiges, mais aucun encaissement dans la fenêtre : tout le volume est plus ancien.",
    "gw.console.disputeRates.overCliff": "Au-delà du seuil Stripe de {cliff} — le compte de la plateforme est en risque.",
    "gw.console.disputeRates.overAlert": "Au-delà du seuil interne de {alert}.",
    "gw.console.disputeRates.ok": "Dans les limites.",
    "gw.console.balances.criteria": "Les quatre seaux de chaque praticien. « Dû » contient les trois autres : en attente + prêt + en cours.",
    "gw.console.balances.colTenant": "Praticien",
    "gw.console.balances.filter.all": "Tous",
    "gw.console.balances.filter.debt": "En dette",
    "gw.console.balances.filter.frozen": "Gelés",
    "gw.console.balances.empty": "Aucun solde de praticien.",
    "gw.console.balances.emptyFiltered.debt": "Aucun praticien ne doit d'argent à la plateforme.",
    "gw.console.balances.emptyFiltered.frozen": "Aucun solde gelé.",
    "gw.console.balances.frozen": "Solde gelé : les reversements sont bloqués.",
    "gw.console.balances.inDebt": "Ce praticien doit de l'argent à la plateforme ; les reversements restent bloqués jusqu'à régularisation.",
    "gw.console.adjustments.criteria": "Un crédit ou un débit manuel contre un praticien. C'est l'outil de récupération après reversement : un débit se déduit du prochain versement.",
    "gw.console.adjustments.tenantId": "Identifiant du praticien",
    "gw.console.adjustments.direction": "Sens",
    "gw.console.adjustments.credit": "Créditer le praticien",
    "gw.console.adjustments.debit": "Débiter le praticien",
    "gw.console.adjustments.amount": "Montant ({currency})",
    "gw.console.adjustments.reason": "Motif",
    "gw.console.adjustments.reasonHint": "Au moins 5 caractères. Conservé dans le journal d'audit.",
    "gw.console.adjustments.record": "Enregistrer l'ajustement",
    "gw.console.adjustments.recorded": "Ajustement enregistré.",
    "gw.console.adjustments.confirmTitle": "Enregistrer un ajustement",
    "gw.console.adjustments.confirmDesc": "Confirmez votre identité : cette écriture déplace de l'argent.",
    "gw.console.adjustments.ackCredit": "Je crédite {tenant} de {amount}.",
    "gw.console.adjustments.ackDebit": "Je débite {tenant} de {amount}.",
    "gw.valid.tenantRequired": "Indiquez l'identifiant du praticien.",
    "gw.valid.directionRequired": "Choisissez de créditer ou de débiter.",
    "gw.valid.amountRequired": "Indiquez un montant.",
    "gw.valid.amountFormat": "Indiquez un montant, par exemple 12,40.",
    "gw.valid.amountNonZero": "Le montant doit être supérieur à zéro.",
    "gw.valid.reasonMin5": "Le motif doit compter au moins 5 caractères.",
    "gw.valid.rateRequired": "Indiquez un taux.",
    "gw.valid.rateFormat": "Indiquez un pourcentage, par exemple 20 ou 17,50.",
    "gw.valid.rateRange": "Le taux ne peut pas dépasser 100 %.",
  },
  en: {
    "gw.console.section.money": "Money",
    "gw.console.section.books": "Books",
    "gw.console.section.policy": "Policy",
    "gw.console.section.operations": "Operations",
    "gw.console.section.overview": "Overview",

    "gw.console.screen.payouts": "Payouts",
    "gw.console.screen.payments": "Payments",
    "gw.console.screen.disputes": "Disputes",
    "gw.console.screen.adjustments": "Adjustments",
    "gw.console.screen.awaitingOperator": "Stuck captures",
    "gw.console.screen.balances": "Practice balances",
    "gw.console.screen.reconciliation": "Stripe reconciliation",
    "gw.console.screen.books": "Do the books balance",
    "gw.console.screen.commission": "Commission",
    "gw.console.screen.consultRates": "Rates to approve",
    "gw.console.screen.rules": "Platform rules",
    "gw.console.screen.disputeRates": "Dispute rates",
    "gw.console.screen.events": "Events we gave up on",
    "gw.console.screen.consults": "Telehealth consults",

    "gw.console.state.asOf": "as of {at}",
    "gw.console.state.neverRead": "never read",
    "gw.console.state.reading": "reading…",
    "gw.console.state.refresh": "refresh",
    "gw.console.state.unreachable": "The gateway did not answer.",
    "gw.console.state.staleWarning": "These are the last figures we know. The refresh failed: {reason}",

    "gw.console.treasury.title": "What the platform is holding",
    "gw.console.treasury.colCurrency": "Currency",
    "gw.console.treasury.colOnHold": "On hold",
    "gw.console.treasury.colReady": "Ready to pay out",
    "gw.console.treasury.colInTransit": "Paying out now",
    "gw.console.treasury.colOwed": "Total owed to practices",
    "gw.console.treasury.noCrossCurrency": "No all-currency total: amounts are never added across currencies.",
    "gw.console.treasury.empty": "No practice balances yet.",
    "gw.console.treasury.unreadable": "Couldn't read what the platform is holding. The amounts are withheld rather than shown as zero.",
    "gw.console.treasury.debt": "{amount} owed back by practices: their payouts are blocked until it clears.",

    "gw.console.overview.title": "What needs you",
    "gw.console.overview.go": "open",
    "gw.console.overview.allClear": "Nothing else needs you.",
    "gw.console.overview.booksBalance": "The books balance.",
    "gw.console.overview.booksDrift": "THE BOOKS DO NOT BALANCE — {amount} out.",
    "gw.console.overview.booksUnknown": "The books could not be checked: {reason}",
    "gw.console.overview.disputeNearCliff": "{count} practice(s) approaching Stripe's threshold: {pct} against {cliff}.",
    "gw.console.overview.disputeOverCliff": "{count} practice(s) are OVER Stripe's {cliff} threshold at {pct}. The platform account is at risk.",
    "gw.console.overview.disputeRatesUnknown": "Dispute rates could not be read: {reason}",
    "gw.console.overview.reconNotRun": "The {date} reconciliation has not run.",
    "gw.console.overview.reconProblems": "{date} reconciliation: {unmatched} Stripe movement(s) with no ledger entry, {mismatched} amount discrepancy(ies).",
    "gw.console.overview.reconUnknown": "The {date} reconciliation could not be read: {reason}",
    "gw.console.overview.payoutsWaiting": "{count} payout(s) waiting for approval.",
    "gw.console.overview.payoutsUnknown": "The payout queue could not be read: {reason}",
    "gw.console.overview.eventsGaveUp": "{count} Stripe event(s) ran out of retries.",
    "gw.console.overview.eventsUnknown": "The abandoned events could not be read: {reason}",
    "gw.console.disputeRates.criteria": "The share of captured payments disputed, per practice, over 90 days. Our own alert sits at {alert}; Stripe closes a platform account above {cliff}.",
    "gw.console.disputeRates.colTenant": "Practice",
    "gw.console.disputeRates.colCaptured": "Captures",
    "gw.console.disputeRates.colDisputed": "Disputed",
    "gw.console.disputeRates.colRate": "Rate",
    "gw.console.disputeRates.colVerdict": "Verdict",
    "gw.console.disputeRates.empty": "No captures in the last 90 days.",
    "gw.console.disputeRates.noVolume": "Disputes but no captures in the window: all of this practice's volume is older.",
    "gw.console.disputeRates.overCliff": "Over Stripe's {cliff} threshold — the platform account is at risk.",
    "gw.console.disputeRates.overAlert": "Over our own {alert} alert threshold.",
    "gw.console.disputeRates.ok": "Within limits.",
    "gw.console.balances.criteria": "Every practice's four buckets. Owed CONTAINS the other three: on hold + ready + paying out.",
    "gw.console.balances.colTenant": "Practice",
    "gw.console.balances.filter.all": "All",
    "gw.console.balances.filter.debt": "In debt",
    "gw.console.balances.filter.frozen": "Frozen",
    "gw.console.balances.empty": "No practice balances.",
    "gw.console.balances.emptyFiltered.debt": "No practice owes the platform money.",
    "gw.console.balances.emptyFiltered.frozen": "No frozen balances.",
    "gw.console.balances.frozen": "Balance frozen: payouts are blocked.",
    "gw.console.balances.inDebt": "This practice owes the platform money; their payouts stay blocked until it clears.",
    "gw.console.adjustments.criteria": "A manual credit or debit against one practice. This is the after-payout recovery tool: a debit is deducted from their next payout.",
    "gw.console.adjustments.tenantId": "Practice id",
    "gw.console.adjustments.direction": "Direction",
    "gw.console.adjustments.credit": "Credit the practice",
    "gw.console.adjustments.debit": "Debit the practice",
    "gw.console.adjustments.amount": "Amount ({currency})",
    "gw.console.adjustments.reason": "Reason",
    "gw.console.adjustments.reasonHint": "At least 5 characters. Kept in the audit log.",
    "gw.console.adjustments.record": "Record adjustment",
    "gw.console.adjustments.recorded": "Adjustment recorded.",
    "gw.console.adjustments.confirmTitle": "Record an adjustment",
    "gw.console.adjustments.confirmDesc": "Confirm your identity — this entry moves money.",
    "gw.console.adjustments.ackCredit": "I am crediting {tenant} {amount}.",
    "gw.console.adjustments.ackDebit": "I am debiting {tenant} {amount}.",
    "gw.valid.tenantRequired": "Enter the practice id.",
    "gw.valid.directionRequired": "Choose whether to credit or debit.",
    "gw.valid.amountRequired": "Enter an amount.",
    "gw.valid.amountFormat": "Enter an amount, for example 12.40.",
    "gw.valid.amountNonZero": "The amount must be more than zero.",
    "gw.valid.reasonMin5": "The reason must be at least 5 characters.",
    "gw.valid.rateRequired": "Enter a rate.",
    "gw.valid.rateFormat": "Enter a percentage, for example 20 or 17.50.",
    "gw.valid.rateRange": "The rate cannot be more than 100%.",
  },
  es: {
    "gw.console.section.money": "Dinero",
    "gw.console.section.books": "Cuentas",
    "gw.console.section.policy": "Reglas",
    "gw.console.section.operations": "Operación",
    "gw.console.section.overview": "Resumen",

    "gw.console.screen.payouts": "Pagos a profesionales",
    "gw.console.screen.payments": "Cobros",
    "gw.console.screen.disputes": "Disputas",
    "gw.console.screen.adjustments": "Ajustes",
    "gw.console.screen.awaitingOperator": "Cobros bloqueados",
    "gw.console.screen.balances": "Saldos de profesionales",
    "gw.console.screen.reconciliation": "Conciliación con Stripe",
    "gw.console.screen.books": "¿Cuadran las cuentas?",
    "gw.console.screen.commission": "Comisión",
    "gw.console.screen.consultRates": "Tarifas por aprobar",
    "gw.console.screen.rules": "Reglas de la plataforma",
    "gw.console.screen.disputeRates": "Tasa de disputas",
    "gw.console.screen.events": "Eventos abandonados",
    "gw.console.screen.consults": "Teleconsultas",

    "gw.console.state.asOf": "a las {at}",
    "gw.console.state.neverRead": "nunca leído",
    "gw.console.state.reading": "leyendo…",
    "gw.console.state.refresh": "actualizar",
    "gw.console.state.unreachable": "La pasarela no respondió.",
    "gw.console.state.staleWarning": "Estas son las últimas cifras conocidas. La actualización falló: {reason}",

    "gw.console.treasury.title": "Lo que la plataforma retiene",
    "gw.console.treasury.colCurrency": "Moneda",
    "gw.console.treasury.colOnHold": "En retención",
    "gw.console.treasury.colReady": "Listo para pagar",
    "gw.console.treasury.colInTransit": "Pagándose ahora",
    "gw.console.treasury.colOwed": "Total debido a profesionales",
    "gw.console.treasury.noCrossCurrency": "Sin total de todas las monedas: los importes nunca se suman entre monedas.",
    "gw.console.treasury.empty": "Todavía no hay saldos de profesionales.",
    "gw.console.treasury.unreadable": "No se pudo leer lo que la plataforma retiene. Los importes se omiten en lugar de mostrarse como cero.",
    "gw.console.treasury.debt": "{amount} que los profesionales deben devolver: sus pagos quedan bloqueados hasta regularizarlo.",

    "gw.console.overview.title": "Lo que requiere su atención",
    "gw.console.overview.go": "abrir",
    "gw.console.overview.allClear": "Nada más requiere su atención.",
    "gw.console.overview.booksBalance": "Las cuentas cuadran.",
    "gw.console.overview.booksDrift": "LAS CUENTAS NO CUADRAN — desvío de {amount}.",
    "gw.console.overview.booksUnknown": "No se pudieron verificar las cuentas: {reason}",
    "gw.console.overview.disputeNearCliff": "{count} profesional(es) se acercan al umbral de Stripe: {pct} frente a {cliff}.",
    "gw.console.overview.disputeOverCliff": "{count} profesional(es) SUPERAN el umbral de Stripe del {cliff} con {pct}. La cuenta de la plataforma está en riesgo.",
    "gw.console.overview.disputeRatesUnknown": "No se pudieron leer las tasas de disputa: {reason}",
    "gw.console.overview.reconNotRun": "La conciliación del {date} no se ha ejecutado.",
    "gw.console.overview.reconProblems": "Conciliación del {date}: {unmatched} movimiento(s) de Stripe sin asiento, {mismatched} discrepancia(s) de importe.",
    "gw.console.overview.reconUnknown": "No se pudo leer la conciliación del {date}: {reason}",
    "gw.console.overview.payoutsWaiting": "{count} pago(s) esperando aprobación.",
    "gw.console.overview.payoutsUnknown": "No se pudo leer la cola de pagos: {reason}",
    "gw.console.overview.eventsGaveUp": "{count} evento(s) de Stripe agotaron sus reintentos.",
    "gw.console.overview.eventsUnknown": "No se pudieron leer los eventos abandonados: {reason}",
    "gw.console.disputeRates.criteria": "Proporción de cobros disputados por profesional, en 90 días. Nuestra alerta está en {alert}; Stripe cierra una cuenta de plataforma por encima del {cliff}.",
    "gw.console.disputeRates.colTenant": "Profesional",
    "gw.console.disputeRates.colCaptured": "Cobros",
    "gw.console.disputeRates.colDisputed": "Disputados",
    "gw.console.disputeRates.colRate": "Tasa",
    "gw.console.disputeRates.colVerdict": "Veredicto",
    "gw.console.disputeRates.empty": "Sin cobros en los últimos 90 días.",
    "gw.console.disputeRates.noVolume": "Disputas pero ningún cobro en la ventana: todo el volumen es anterior.",
    "gw.console.disputeRates.overCliff": "Por encima del umbral de Stripe del {cliff} — la cuenta de la plataforma está en riesgo.",
    "gw.console.disputeRates.overAlert": "Por encima de nuestro umbral de alerta del {alert}.",
    "gw.console.disputeRates.ok": "Dentro de los límites.",
    "gw.console.balances.criteria": "Los cuatro saldos de cada profesional. Debido CONTIENE los otros tres: en retención + listo + pagándose.",
    "gw.console.balances.colTenant": "Profesional",
    "gw.console.balances.filter.all": "Todos",
    "gw.console.balances.filter.debt": "Con deuda",
    "gw.console.balances.filter.frozen": "Congelados",
    "gw.console.balances.empty": "Sin saldos de profesionales.",
    "gw.console.balances.emptyFiltered.debt": "Ningún profesional debe dinero a la plataforma.",
    "gw.console.balances.emptyFiltered.frozen": "Ningún saldo congelado.",
    "gw.console.balances.frozen": "Saldo congelado: los pagos están bloqueados.",
    "gw.console.balances.inDebt": "Este profesional debe dinero a la plataforma; sus pagos siguen bloqueados hasta regularizarlo.",
    "gw.console.adjustments.criteria": "Un crédito o cargo manual contra un profesional. Es la herramienta de recuperación tras el pago: un cargo se descuenta de su próximo pago.",
    "gw.console.adjustments.tenantId": "Id del profesional",
    "gw.console.adjustments.direction": "Sentido",
    "gw.console.adjustments.credit": "Abonar al profesional",
    "gw.console.adjustments.debit": "Cargar al profesional",
    "gw.console.adjustments.amount": "Importe ({currency})",
    "gw.console.adjustments.reason": "Motivo",
    "gw.console.adjustments.reasonHint": "Mínimo 5 caracteres. Se conserva en el registro de auditoría.",
    "gw.console.adjustments.record": "Registrar ajuste",
    "gw.console.adjustments.recorded": "Ajuste registrado.",
    "gw.console.adjustments.confirmTitle": "Registrar un ajuste",
    "gw.console.adjustments.confirmDesc": "Confirme su identidad: este asiento mueve dinero.",
    "gw.console.adjustments.ackCredit": "Estoy abonando {amount} a {tenant}.",
    "gw.console.adjustments.ackDebit": "Estoy cargando {amount} a {tenant}.",
    "gw.valid.tenantRequired": "Indique el id del profesional.",
    "gw.valid.directionRequired": "Elija abonar o cargar.",
    "gw.valid.amountRequired": "Indique un importe.",
    "gw.valid.amountFormat": "Indique un importe, por ejemplo 12,40.",
    "gw.valid.amountNonZero": "El importe debe ser mayor que cero.",
    "gw.valid.reasonMin5": "El motivo debe tener al menos 5 caracteres.",
    "gw.valid.rateRequired": "Indique una tasa.",
    "gw.valid.rateFormat": "Indique un porcentaje, por ejemplo 20 o 17,50.",
    "gw.valid.rateRange": "La tasa no puede superar el 100 %.",
  },
};
